import { useEffect } from 'react';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { isInputElementFocused } from './utils';

interface UseDraftNavigationProps {
  filteredCommunications: UserCommunication[];
  onOpenDraft?: () => void;
}

export function useDraftNavigation({
  filteredCommunications,
  onOpenDraft,
}: UseDraftNavigationProps) {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const { selectedEmailIndex, setSelectedEmailIndex } = useShortcutsContext();

  useEffect(() => {
    registerShortcut({
      key: ShortcutKey.NEXT,
      contexts: [ShortcutContext.DRAFT],
      handler: () => {
        const maxIndex = filteredCommunications.length - 1;
        const nextIndex = Math.min(selectedEmailIndex + 1, maxIndex);
        setSelectedEmailIndex(nextIndex);

        const element = document.querySelector(
          `[data-email-index="${nextIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      },
      description: 'Move to next draft',
    });

    registerShortcut({
      key: ShortcutKey.PREVIOUS,
      contexts: [ShortcutContext.DRAFT],
      handler: () => {
        const prevIndex = Math.max(selectedEmailIndex - 1, 0);
        setSelectedEmailIndex(prevIndex);

        const element = document.querySelector(
          `[data-email-index="${prevIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      },
      description: 'Move to previous draft',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_DOWN,
      contexts: [ShortcutContext.DRAFT],
      handler: () => {
        const maxIndex = filteredCommunications.length - 1;
        const nextIndex = Math.min(selectedEmailIndex + 1, maxIndex);
        setSelectedEmailIndex(nextIndex);

        const element = document.querySelector(
          `[data-email-index="${nextIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      },
      description: 'Move to next draft',
    });

    registerShortcut({
      key: ShortcutKey.ARROW_UP,
      contexts: [ShortcutContext.DRAFT],
      handler: () => {
        const prevIndex = Math.max(selectedEmailIndex - 1, 0);
        setSelectedEmailIndex(prevIndex);

        const element = document.querySelector(
          `[data-email-index="${prevIndex}"]`,
        );
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      },
      description: 'Move to previous draft',
    });

    const openDraft = () => {
      if (isInputElementFocused()) {
        return;
      }

      if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length &&
        onOpenDraft
      ) {
        onOpenDraft();
      }
    };

    registerShortcut({
      key: ShortcutKey.OPEN,
      contexts: [ShortcutContext.DRAFT],
      handler: openDraft,
      description: 'Open selected draft',
    });

    registerShortcut({
      key: ShortcutKey.ENTER,
      contexts: [ShortcutContext.DRAFT],
      handler: openDraft,
      description: 'Open selected draft',
    });

    const ctx = [ShortcutContext.DRAFT];
    return () => {
      unregisterShortcut(ShortcutKey.NEXT, ctx);
      unregisterShortcut(ShortcutKey.PREVIOUS, ctx);
      unregisterShortcut(ShortcutKey.ARROW_DOWN, ctx);
      unregisterShortcut(ShortcutKey.ARROW_UP, ctx);
      unregisterShortcut(ShortcutKey.OPEN, ctx);
      unregisterShortcut(ShortcutKey.ENTER, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    setSelectedEmailIndex,
    filteredCommunications,
    onOpenDraft,
  ]);

  useEffect(() => {
    if (filteredCommunications.length === 0) {
      setSelectedEmailIndex(-1);
    } else if (selectedEmailIndex === -1 && filteredCommunications.length > 0) {
      setSelectedEmailIndex(0);
    } else if (selectedEmailIndex >= filteredCommunications.length) {
      setSelectedEmailIndex(filteredCommunications.length - 1);
    }
  }, [
    filteredCommunications.length,
    selectedEmailIndex,
    setSelectedEmailIndex,
  ]);

  return {
    selectedEmailIndex,
    setSelectedEmailIndex,
    selectedDraft:
      selectedEmailIndex >= 0 &&
      selectedEmailIndex < filteredCommunications.length
        ? filteredCommunications[selectedEmailIndex]
        : null,
  };
}
